import React, { useCallback } from 'react';
import { View, StyleSheet, Image } from 'react-native';
import { Text } from '@/components/common/text';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { useAppDispatch } from '@/store';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { strings } from '@/constants/residents/strings';
import { setSelectedBulletinId } from '@/store/slices/residents/homeSlice';
import { NOTIFICATIONS_MAX_MARK } from '@/constants/residents/notifications';
import { useNotifications, NotificationItem } from '@/hooks/common/useNotifications';
import { useMarketplaceFeedbackModal } from '@/hooks/common/useMarketplaceFeedbackModal';
import { NotificationList } from '@/components/residents/notification/NotificationList';
import { LeaveFeedbackModal } from '@/components/residents/marketplace';
import {
  isMarketplaceRelated,
  isPropertyChat as isPropertyChatHelper,
  getListingImageUrl,
} from '@/utils/residents/notificationHelpers';

type Props = {
  onClose: () => void;
  navigateToAll: () => void;
  navigateToSettings?: () => void;
  navigateToChat?: () => void;
};

export const DesktopNotificationsPopover: React.FC<Props> = ({ onClose, navigateToAll, navigateToSettings, navigateToChat }) => {
  const { colors } = useThemeColors();
  const dispatch = useAppDispatch();
  const { notifications, isLoading, isUnread, markFirstNAsRead, formatDateMMMdd, isMarketplaceFeedback, reload, markPropertyChatAsViewed } = useNotifications();

  const feedbackModal = useMarketplaceFeedbackModal({ onSuccess: reload });

  React.useEffect(() => {
    if (Array.isArray(notifications) && notifications.length > 0) {
      markFirstNAsRead(notifications, NOTIFICATIONS_MAX_MARK);
    }
  }, [notifications, markFirstNAsRead]);

  const handleNotificationPress = useCallback((b: NotificationItem) => {
    if (isMarketplaceFeedback(b)) {
      feedbackModal.openModal(b);
    } else if (isPropertyChatHelper(b)) {
      markPropertyChatAsViewed();
      onClose();
      if (navigateToChat) {
        navigateToChat();
      }
    } else {
      dispatch(setSelectedBulletinId(b.id));
      navigateToAll();
    }
  }, [dispatch, navigateToAll, navigateToChat, onClose, isMarketplaceFeedback, feedbackModal, markPropertyChatAsViewed]);

  const renderRow = (b: NotificationItem) => {
    const isPropertyChatItem = isPropertyChatHelper(b);
    const isMarketplace = isMarketplaceRelated(b);
    const listingImageUrl = getListingImageUrl(b);

    const IconComponent = isPropertyChatItem
      ? RESIDENTS_SVG_COMPONENTS.CHAT_BUBBLE
      : isMarketplace
        ? RESIDENTS_SVG_COMPONENTS.SUPERMARKET
        : RESIDENTS_SVG_COMPONENTS.PROPERTY;

    return (
      <Hoverable hover="row"
        key={String(b.id)}
        accessibilityRole="button"
        onPress={() => handleNotificationPress(b)}
        style={styles.row}
      >
        <View style={styles.avatarWrap}>
          <View style={[styles.avatarCircle, { backgroundColor: colors.activityIcon.bg, borderColor: colors.activityIcon.border }]}>
            {listingImageUrl ? (
              <Image
                source={{ uri: listingImageUrl }}
                style={styles.avatarImage}
                resizeMode="cover"
              />
            ) : (
              <IconComponent width={22} height={22} color={colors.text.muted}/>
            )}
            {isUnread(b) && (
              <View style={[styles.unreadBubble, { backgroundColor: colors.accent.red, borderColor: colors.accent.red }]} accessibilityLabel="Unread notification" />
            )}
          </View>
        </View>
        <View style={styles.rowTextWrap}>
          <Text numberOfLines={1} style={[styles.rowTitle, { color: colors.text.primary }]}>{b.title || b.responseDescription || strings.notifications.desktop.defaultTitle}</Text>
          <Text numberOfLines={2} style={[styles.rowSub, { color: colors.text.subtle }]}>{b.description || b.descriptionHtml || ''}</Text>
        </View>
        <View style={styles.rowDateWrap}>
          <Text style={[styles.rowDate, { color: colors.text.dim }]}>{formatDateMMMdd(b.createdAt)}</Text>
        </View>
      </Hoverable>
    );
  };

  return (
    <View style={styles.wrapper}>
      <Hoverable hover="none" pressedOpacity={1} style={styles.overlay} onPress={onClose} />
      <View style={[styles.popover, { backgroundColor: colors.bg.base, borderColor: colors.card.border }]}>
        <View style={styles.header}>
          <Text style={[styles.headerTitle, { color: colors.text.primary }]}>{strings.notifications.desktop.title}</Text>
          <Hoverable hover="glass"
            accessibilityRole="button"
            accessibilityLabel={strings.notifications.settings.title}
            onPress={() => (navigateToSettings ? navigateToSettings() : navigateToAll())}
            style={[styles.settingsButton, { backgroundColor: colors.glass.buttonBg, borderColor: colors.glass.buttonBorder }]}
          >
            <RESIDENTS_SVG_COMPONENTS.COG width={20} height={20} color={colors.text.muted} />
          </Hoverable>
        </View>
        <View style={[styles.divider, { backgroundColor: colors.separator }]} />
        <View style={styles.listWrap}>
          <NotificationList
            data={notifications}
            isLoading={isLoading}
            emptyText={strings.notifications.desktop.noNew}
            onPressItem={handleNotificationPress}
            showDateSmall={false}
            rowVariant={'popover'}
            showSeparators={false}
            renderRight={(b: NotificationItem) => (
              <View style={styles.rowDateWrap}>
                <Text style={[styles.rowDate, { color: colors.text.dim }]}>{formatDateMMMdd(b.createdAt)}</Text>
              </View>
            )}
          />
        </View>
        <View style={[styles.footer, { borderTopColor: colors.separator }]}>
          <Hoverable hover="link" accessibilityRole="button" onPress={navigateToAll}>
            {({ textStyle }) => <Text style={[styles.footerLink, textStyle]}>{strings.notifications.desktop.seeAll}</Text>}
          </Hoverable>
        </View>
      </View>

      <LeaveFeedbackModal
        visible={feedbackModal.isVisible}
        feedbackRequest={feedbackModal.feedbackRequest}
        onClose={feedbackModal.closeModal}
        onSubmit={feedbackModal.submitFeedback}
        isSubmitting={feedbackModal.isSubmitting}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    position: 'absolute',
    top: 40,
    right: 0,
    zIndex: 1100,
    elevation: 24,
  },
  overlay: {
    position: 'fixed' as any,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  popover: {
    width: 400,
    maxHeight: 560,
    borderWidth: 1,
    borderRadius: 16,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  headerTitle: {
    ...REDESIGN_TYPOGRAPHY.cardTitleLarge,
    fontWeight: '700',
  },
  settingsButton: {
    width: 36,
    height: 36,
    borderRadius: 10,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  divider: {
    height: 1,
  },
  listWrap: {
    flexGrow: 1,
    maxHeight: 460,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  avatarWrap: {
    paddingRight: 12,
  },
  avatarCircle: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  avatarImage: {
    width: 36,
    height: 36,
    borderRadius: 18,
  },
  unreadBubble: {
    position: 'absolute',
    top: -2,
    left: -2,
    width: 12,
    height: 12,
    borderRadius: 6,
    borderWidth: 1,
  },
  rowTextWrap: {
    flex: 1,
    paddingRight: 8,
  },
  rowTitle: {
    ...REDESIGN_TYPOGRAPHY.cardTitle,
    fontWeight: '700',
  },
  rowSub: {
    ...REDESIGN_TYPOGRAPHY.caption,
    marginTop: 2,
  },
  rowDateWrap: {
    width: 52,
    alignItems: 'flex-end',
  },
  rowDate: {
    ...REDESIGN_TYPOGRAPHY.caption,
  },
  footer: {
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderTopWidth: 1,
  },
  footerLink: {
    ...REDESIGN_TYPOGRAPHY.captionMedium,
    color: PRIMITIVE_COLORS.resioBlueLight,
    fontWeight: '600',
  },
});

export default DesktopNotificationsPopover;
